import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { Modal } from '../../components/ui/Modal/Modal';
import { Input } from '../../components/ui/Input/Input';
import { Textarea } from '../../components/ui/Textarea/Textarea';
import { Switch } from '../../components/ui/Switch/Switch';
import { apiFetch } from '../../lib/api';

interface UseCase {
  id: string;
  label: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Common Lucide icon names for reference
const SUGGESTED_ICONS = [
  'Share2', 'Link', 'FileText', 'BookOpen', 'Package',
  'Megaphone', 'Presentation', 'Play', 'Palette', 'GraduationCap',
  'Image', 'Camera', 'Video', 'Music', 'Globe',
  'ShoppingBag', 'Users', 'Mail', 'Calendar', 'Star'
];

export function UseCases() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [editingUseCase, setEditingUseCase] = React.useState<UseCase | null>(null);

  // Form state
  const [formId, setFormId] = React.useState('');
  const [formLabel, setFormLabel] = React.useState('');
  const [formDescription, setFormDescription] = React.useState('');
  const [formIcon, setFormIcon] = React.useState('');
  const [formEnabled, setFormEnabled] = React.useState(true);

  // Fetch use cases
  const { data: useCases, isLoading } = useQuery({
    queryKey: ['admin', 'use-cases'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/use-cases');
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load');
      return json.data as UseCase[];
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: { id: string; label: string; description?: string; icon?: string; enabled?: boolean }) => {
      const res = await apiFetch('/api/admin/use-cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to create');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'use-cases'] });
      handleCloseModal();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; label?: string; description?: string | null; icon?: string | null; enabled?: boolean; sort_order?: number }) => {
      const res = await apiFetch(`/api/admin/use-cases/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to update');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'use-cases'] });
      handleCloseModal();
    },
  });

  // Toggle enabled mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await apiFetch(`/api/admin/use-cases/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to toggle');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'use-cases'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/admin/use-cases/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to delete');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'use-cases'] });
    },
  });

  const handleOpenAddModal = () => {
    setFormId('');
    setFormLabel('');
    setFormDescription('');
    setFormIcon('');
    setFormEnabled(true);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (useCase: UseCase) => {
    setEditingUseCase(useCase);
    setFormLabel(useCase.label);
    setFormDescription(useCase.description || '');
    setFormIcon(useCase.icon || '');
    setFormEnabled(useCase.enabled);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingUseCase(null);
    setFormId('');
    setFormLabel('');
    setFormDescription('');
    setFormIcon('');
    setFormEnabled(true);
  };

  const handleCreate = () => {
    if (!formId.trim() || !formLabel.trim()) return;
    createMutation.mutate({
      id: formId.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'),
      label: formLabel.trim(),
      description: formDescription.trim() || undefined,
      icon: formIcon.trim() || undefined,
      enabled: formEnabled,
    });
  };

  const handleUpdate = () => {
    if (!editingUseCase || !formLabel.trim()) return;
    updateMutation.mutate({
      id: editingUseCase.id,
      label: formLabel.trim(),
      description: formDescription.trim() || null,
      icon: formIcon.trim() || null,
      enabled: formEnabled,
    });
  };

  // Auto-generate ID from label
  const handleLabelChange = (value: string) => {
    setFormLabel(value);
    if (showAddModal && !editingUseCase) {
      setFormId(value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Use Cases"
        description="Configure use cases shown during onboarding. Users select what they'll use Cascady for."
      >
        <Button onClick={handleOpenAddModal}>Add Use Case</Button>
      </PageHeader>

      {isLoading ? (
        <div className="text-gray-500">Loading...</div>
      ) : !useCases?.length ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No use cases configured. Add one to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {useCases.map((useCase) => (
            <Card key={useCase.id}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {useCase.icon && (
                        <span className="text-gray-400 text-sm font-mono">{useCase.icon}</span>
                      )}
                      <h3 className="font-medium text-gray-900">{useCase.label}</h3>
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                        {useCase.id}
                      </code>
                      {useCase.enabled ? (
                        <Badge variant="success">Enabled</Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </div>
                    {useCase.description && (
                      <p className="text-sm text-gray-500 mt-1">{useCase.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-500">Enabled</span>
                      <Switch
                        checked={useCase.enabled}
                        onChange={(e) => toggleMutation.mutate({ id: useCase.id, enabled: e.target.checked })}
                      />
                    </label>

                    <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(useCase)}>
                      Edit
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Delete this use case? This will not affect organizations that already selected it.')) {
                          deleteMutation.mutate(useCase.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <Modal isOpen={showAddModal} onClose={handleCloseModal} title="Add Use Case">
        <div className="space-y-4">
          <Input
            label="Label"
            value={formLabel}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder="e.g. Social Media Posts"
          />

          <Input
            label="ID (auto-generated)"
            value={formId}
            onChange={(e) => setFormId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="e.g. social_posts"
            hint="Lowercase letters, numbers, and underscores only"
          />

          <Textarea
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="e.g. Instagram, Facebook, Twitter/X graphics"
            rows={2}
          />

          <Input
            label="Icon (Lucide icon name)"
            value={formIcon}
            onChange={(e) => setFormIcon(e.target.value)}
            placeholder="e.g. Share2"
            hint={`Suggestions: ${SUGGESTED_ICONS.slice(0, 5).join(', ')}`}
          />

          <label className="flex items-center gap-2">
            <Switch checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
            <span className="text-sm">Enabled (show in onboarding)</span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleCloseModal}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!formId.trim() || !formLabel.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>

          {createMutation.error && (
            <div className="text-sm text-red-600">{createMutation.error.message}</div>
          )}
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editingUseCase} onClose={handleCloseModal} title="Edit Use Case">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
            <code className="text-sm bg-gray-100 px-2 py-1 rounded">{editingUseCase?.id}</code>
            <p className="text-xs text-gray-500 mt-1">ID cannot be changed after creation</p>
          </div>

          <Input
            label="Label"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            placeholder="e.g. Social Media Posts"
          />

          <Textarea
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="e.g. Instagram, Facebook, Twitter/X graphics"
            rows={2}
          />

          <Input
            label="Icon (Lucide icon name)"
            value={formIcon}
            onChange={(e) => setFormIcon(e.target.value)}
            placeholder="e.g. Share2"
            hint={`Suggestions: ${SUGGESTED_ICONS.slice(0, 5).join(', ')}`}
          />

          <label className="flex items-center gap-2">
            <Switch checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
            <span className="text-sm">Enabled (show in onboarding)</span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleCloseModal}>Cancel</Button>
            <Button
              onClick={handleUpdate}
              disabled={!formLabel.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {updateMutation.error && (
            <div className="text-sm text-red-600">{updateMutation.error.message}</div>
          )}
        </div>
      </Modal>
    </PageContainer>
  );
}
